import apiClient from './apiClient';

export type PurchaseOrderStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'sent'
  | 'confirmed'
  | 'partial_received'
  | 'received'
  | 'closed'
  | 'cancelled';

export type GoodsReceiptStatus =
  | 'draft'
  | 'pending_acceptance'
  | 'accepted'
  | 'rejected'
  | 'disputed';

export type ProcurementRiskStatus =
  | 'on_hand'
  | 'on_order_in_time'
  | 'on_order_late'
  | 'not_ordered';

export interface ResourceRef {
  id: string;
  name: string;
  resourceCode?: string;
  unitOfMeasure?: string;
}

export interface StorageLocationRef {
  id: string;
  name: string;
  locationCode?: string;
}

export interface StorageZoneRef {
  id: string;
  storageLocationId: string;
  zoneCode: string;
  name: string;
  zoneType?: string;
  protectionClass?: string | null;
}

export interface PurchaseOrderItemDTO {
  id: string;
  purchaseOrderId: string;
  resourceId: string;
  storageLocationId?: string | null;
  storageZoneId?: string | null;
  resource?: ResourceRef;
  storageLocation?: StorageLocationRef;
  storageZone?: StorageZoneRef;
  taskResourceLinks?: Array<{
    id: string;
    taskResourceId: string;
    quantityLinked: number;
    taskResource?: { id: string; taskId: string; resourceId: string; quantityRequired: number };
  }>;
  description?: string | null;
  quantityOrdered: number;
  unitCost: number;
  quantityReceived: number;
  quantityRejected: number;
  expectedDeliveryDate?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface PurchaseOrder {
  id: string;
  projectId: string;
  supplierId: string;
  supplier?: { id: string; name: string };
  poNumber: string;
  status: PurchaseOrderStatus;
  expectedDeliveryDate?: string | null;
  approvalThresholdAtCreation: number;
  totalAmount: number;
  submittedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  sentAt?: string | null;
  confirmedAt?: string | null;
  cancelledAt?: string | null;
  closedAt?: string | null;
  closeShortAt?: string | null;
  createdBy?: string | null;
  approvedBy?: string | null;
  rejectedBy?: string | null;
  notes?: string | null;
  items?: PurchaseOrderItemDTO[];
  goodsReceipts?: GoodsReceipt[];
  accountsTransactions?: Array<{
    id: string;
    status: 'draft' | 'recorded' | 'reconciled';
    amount: number;
    transactionDate: string;
    description: string;
    goodsReceiptId?: string | null;
  }>;
  createdAt?: string;
  updatedAt?: string;
}

export interface GoodsReceiptItemDTO {
  id: string;
  goodsReceiptId: string;
  purchaseOrderItemId: string;
  resourceId: string;
  storageLocationId: string;
  storageZoneId?: string | null;
  resource?: ResourceRef;
  storageLocation?: StorageLocationRef;
  storageZone?: StorageZoneRef;
  quantityReceived: number;
  quantityAccepted: number;
  quantityRejected: number;
  unitCost?: number | null;
  inspectionStatus: 'pending' | 'accepted' | 'rejected' | 'disputed';
  rejectionReason?: string | null;
  notes?: string | null;
}

export interface GoodsReceipt {
  id: string;
  purchaseOrderId: string;
  projectId: string;
  supplierId: string;
  supplier?: { id: string; name: string };
  purchaseOrder?: { id: string; poNumber: string; expectedDeliveryDate?: string | null };
  grnNumber: string;
  status: GoodsReceiptStatus;
  receivedAt?: string | null;
  submittedAt?: string | null;
  acceptedAt?: string | null;
  rejectedAt?: string | null;
  disputedAt?: string | null;
  createdBy?: string | null;
  acceptedBy?: string | null;
  rejectedBy?: string | null;
  notes?: string | null;
  items?: GoodsReceiptItemDTO[];
  createdAt?: string;
}

export interface AtRiskTaskResource {
  taskId: string;
  taskResourceId?: string;
  taskCode?: string;
  taskTitle?: string;
  resourceId: string;
  resourceName?: string;
  required: number;
  available: number;
  shortage: number;
  procurementStatus: ProcurementRiskStatus;
  blockedReason?: string;
  procurementSpecification?: string | null;
  plannedStartDate?: string | null;
  purchaseOrderId?: string | null;
  purchaseOrderItemId?: string | null;
  expectedDeliveryDate?: string | null;
  /** Optional coverage fields (newer backend); may be absent — always read defensively. */
  procurementCoveredQuantity?: number | null;
  procurementShortfallQuantity?: number | null;
}

export interface PurchaseOrderListFilters {
  projectId?: string;
  status?: PurchaseOrderStatus | PurchaseOrderStatus[];
  supplierId?: string;
  orderedFrom?: string;
  orderedTo?: string;
  expectedDeliveryFrom?: string;
  expectedDeliveryTo?: string;
  page?: number;
  limit?: number;
}

export interface GoodsReceiptListFilters {
  projectId?: string;
  status?: GoodsReceiptStatus | GoodsReceiptStatus[];
  purchaseOrderId?: string;
  receivedFrom?: string;
  receivedTo?: string;
  page?: number;
  limit?: number;
}

export interface PaginatedResult<T> {
  rows: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ProcurementCatalog {
  suppliers: Array<{ id: string; name: string; contactPerson?: string | null; email?: string | null; phone?: string | null; leadTimeDays?: number | null }>;
  resources: Array<{
    id: string;
    name: string;
    resourceCode: string;
    unitOfMeasure: string;
    resourceType?: string | null;
    category?: string | null;
    unitCost?: number | string | null;
    preferredSupplierId?: string | null;
    leadTimeDays?: number | null;
  }>;
  storageLocations: Array<{ id: string; name: string; locationCode: string; locationType?: string }>;
  storageZones: StorageZoneRef[];
}

export interface SupplierSlippageRow {
  supplierId: string;
  supplierName: string;
  orderCount?: number;
  openOrderCount?: number;
  lateOpenOrderCount?: number;
  receivedOrderCount?: number;
  totalOrderValue?: number;
  receiptCount: number;
  averageSlipDays: number;
  lateReceiptCount: number;
  onTimeReceiptCount: number;
  worstSlipDays: number;
}

export interface SupervisorTeamAssignment {
  id: string;
  projectId: string;
  supervisorUserId: string;
  teamId: string;
  startsAt?: string;
  endsAt?: string | null;
  isActive: boolean;
  project?: { id: string; name: string; projectCode: string };
  supervisor?: { id: string; email: string; firstName: string; lastName: string; role: string };
  team?: { id: string; name: string; teamType: string };
}

type Envelope<T> = { success?: boolean; data?: T; error?: string };

const unwrap = <T>(response: unknown): T => {
  const r = response as { data?: unknown; status?: number };
  const body = typeof r?.status === 'number' ? r.data : response;
  const env = body as Envelope<T>;
  return env && Object.prototype.hasOwnProperty.call(env, 'data')
    ? (env.data as T)
    : (body as T);
};

const buildQuery = (filters: Record<string, unknown>): string => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((v) => params.append(key, String(v)));
    } else {
      params.append(key, String(value));
    }
  });
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

class ProcurementService {
  private poFilters(filters: PurchaseOrderListFilters): Record<string, unknown> {
    return {
      project_id: filters.projectId,
      supplier_id: filters.supplierId,
      status: Array.isArray(filters.status) ? filters.status.join(',') : filters.status,
      ordered_from: filters.orderedFrom,
      ordered_to: filters.orderedTo,
      expected_delivery_from: filters.expectedDeliveryFrom,
      expected_delivery_to: filters.expectedDeliveryTo,
      page: filters.page,
      limit: filters.limit,
    };
  }

  private grnFilters(filters: GoodsReceiptListFilters): Record<string, unknown> {
    return {
      project_id: filters.projectId,
      po_id: filters.purchaseOrderId,
      status: Array.isArray(filters.status) ? filters.status.join(',') : filters.status,
      received_from: filters.receivedFrom,
      received_to: filters.receivedTo,
      page: filters.page,
      limit: filters.limit,
    };
  }

  async listPurchaseOrders(filters: PurchaseOrderListFilters = {}): Promise<PaginatedResult<PurchaseOrder>> {
    const response = await apiClient.get<Envelope<any>>(`/purchase-orders${buildQuery(this.poFilters(filters))}`);
    const result = unwrap<any>(response) ?? {};
    return {
      rows: result.purchaseOrders ?? result.rows ?? [],
      total: result.total ?? 0,
      page: result.page ?? 1,
      limit: result.limit ?? 20,
      totalPages: result.totalPages ?? 1,
    };
  }

  async getPurchaseOrder(id: string): Promise<PurchaseOrder> {
    const response = await apiClient.get<Envelope<PurchaseOrder>>(`/purchase-orders/${id}`);
    return unwrap<PurchaseOrder>(response);
  }

  async createPurchaseOrder(input: {
    projectId: string;
    supplierId: string;
    poNumber?: string;
    expectedDeliveryDate?: string;
    notes?: string;
    items?: Array<{
      resourceId: string;
      quantityOrdered: number;
      unitCost?: number;
      storageLocationId?: string;
      storageZoneId?: string | null;
      description?: string;
      expectedDeliveryDate?: string;
    }>;
  }): Promise<PurchaseOrder> {
    const response = await apiClient.post<Envelope<PurchaseOrder>>('/purchase-orders', input);
    return unwrap<PurchaseOrder>(response);
  }

  async updatePurchaseOrder(id: string, input: Partial<{ supplierId: string; expectedDeliveryDate: string; notes: string }>): Promise<PurchaseOrder> {
    const response = await apiClient.patch<Envelope<PurchaseOrder>>(`/purchase-orders/${id}`, input);
    return unwrap<PurchaseOrder>(response);
  }

  async addPurchaseOrderItem(purchaseOrderId: string, input: {
    resourceId: string;
    quantityOrdered: number;
    unitCost: number;
    storageLocationId?: string;
    storageZoneId?: string | null;
    description?: string;
    expectedDeliveryDate?: string;
  }): Promise<PurchaseOrder> {
    const response = await apiClient.post<Envelope<PurchaseOrder>>(`/purchase-orders/${purchaseOrderId}/items`, input);
    return unwrap<PurchaseOrder>(response);
  }

  async updatePurchaseOrderItem(itemId: string, input: Partial<{
    resourceId: string;
    quantityOrdered: number;
    unitCost: number;
    storageLocationId: string;
    storageZoneId: string | null;
    description: string;
    expectedDeliveryDate: string;
  }>): Promise<PurchaseOrder> {
    const response = await apiClient.patch<Envelope<PurchaseOrder>>(`/purchase-order-items/${itemId}`, input);
    return unwrap<PurchaseOrder>(response);
  }

  async removePurchaseOrderItem(itemId: string): Promise<PurchaseOrder> {
    const response = await apiClient.delete<Envelope<PurchaseOrder>>(`/purchase-order-items/${itemId}`);
    return unwrap<PurchaseOrder>(response);
  }

  private async transition(id: string, action: string, payload?: Record<string, unknown>): Promise<PurchaseOrder> {
    const response = await apiClient.post<Envelope<PurchaseOrder>>(`/purchase-orders/${id}/${action}`, payload ?? {});
    return unwrap<PurchaseOrder>(response);
  }

  submitPurchaseOrder(id: string) { return this.transition(id, 'submit'); }
  approvePurchaseOrder(id: string) { return this.transition(id, 'approve'); }
  rejectPurchaseOrder(id: string, reason?: string) { return this.transition(id, 'reject', reason ? { reason } : undefined); }
  sendPurchaseOrder(id: string) { return this.transition(id, 'send'); }
  confirmPurchaseOrder(id: string) { return this.transition(id, 'confirm'); }
  cancelPurchaseOrder(id: string, reason?: string) { return this.transition(id, 'cancel', reason ? { reason } : undefined); }
  closeShortPurchaseOrder(id: string) { return this.transition(id, 'close-short'); }
  closePurchaseOrder(id: string) { return this.transition(id, 'close'); }

  async linkPoItemToTaskResource(purchaseOrderItemId: string, taskResourceId: string, quantityLinked: number): Promise<void> {
    await apiClient.post<void>(`/purchase-order-items/${purchaseOrderItemId}/link-task-resource`, { taskResourceId, quantityLinked });
  }

  async unlinkPoTaskResourceLink(linkId: string): Promise<void> {
    await apiClient.delete<void>(`/po-task-resource-links/${linkId}`);
  }

  async listGoodsReceipts(filters: GoodsReceiptListFilters = {}): Promise<PaginatedResult<GoodsReceipt>> {
    const response = await apiClient.get<Envelope<any>>(`/goods-receipts${buildQuery(this.grnFilters(filters))}`);
    const result = unwrap<any>(response) ?? {};
    return {
      rows: result.goodsReceipts ?? result.rows ?? [],
      total: result.total ?? 0,
      page: result.page ?? 1,
      limit: result.limit ?? 20,
      totalPages: result.totalPages ?? 1,
    };
  }

  async getGoodsReceipt(id: string): Promise<GoodsReceipt> {
    const response = await apiClient.get<Envelope<GoodsReceipt>>(`/goods-receipts/${id}`);
    return unwrap<GoodsReceipt>(response);
  }

  async createGoodsReceipt(input: {
    purchaseOrderId: string;
    grnNumber?: string;
    receivedAt?: string;
    items: Array<{
      purchaseOrderItemId: string;
      resourceId?: string;
      storageLocationId?: string;
      storageZoneId?: string | null;
      quantityReceived: number;
      quantityAccepted?: number;
      quantityRejected?: number;
      unitCost?: number;
      rejectionReason?: string;
      notes?: string;
    }>;
    notes?: string;
  }): Promise<GoodsReceipt> {
    const response = await apiClient.post<Envelope<GoodsReceipt>>('/goods-receipts', input);
    return unwrap<GoodsReceipt>(response);
  }

  async submitGoodsReceipt(id: string): Promise<GoodsReceipt> {
    const response = await apiClient.post<Envelope<GoodsReceipt>>(`/goods-receipts/${id}/submit`);
    return unwrap<GoodsReceipt>(response);
  }

  async acceptGoodsReceipt(id: string, items?: Array<{
    id: string;
    quantityAccepted: number;
    quantityRejected: number;
    rejectionReason?: string;
    notes?: string;
  }>): Promise<GoodsReceipt> {
    const response = await apiClient.post<Envelope<GoodsReceipt>>(`/goods-receipts/${id}/accept`, items ? { items } : {});
    return unwrap<GoodsReceipt>(response);
  }

  async rejectGoodsReceipt(id: string, reason?: string): Promise<GoodsReceipt> {
    const response = await apiClient.post<Envelope<GoodsReceipt>>(`/goods-receipts/${id}/reject`, reason ? { reason } : {});
    return unwrap<GoodsReceipt>(response);
  }

  async flagDisputeGoodsReceipt(id: string, reason?: string): Promise<GoodsReceipt> {
    const response = await apiClient.post<Envelope<GoodsReceipt>>(`/goods-receipts/${id}/flag-dispute`, reason ? { reason } : {});
    return unwrap<GoodsReceipt>(response);
  }

  async getAtRisk(params: { projectId?: string; withinDays?: number } = {}): Promise<AtRiskTaskResource[]> {
    const response = await apiClient.get<Envelope<AtRiskTaskResource[]>>(
      `/procurement/at-risk${buildQuery({ project_id: params.projectId, within_days: params.withinDays ?? 14 })}`
    );
    return unwrap<AtRiskTaskResource[]>(response) ?? [];
  }

  async getCatalog(): Promise<ProcurementCatalog> {
    const response = await apiClient.get<Envelope<ProcurementCatalog>>('/procurement/catalog');
    return unwrap<ProcurementCatalog>(response);
  }

  async getSupplierSlippage(params: { projectId?: string; days?: number } = {}): Promise<SupplierSlippageRow[]> {
    const response = await apiClient.get<Envelope<SupplierSlippageRow[]>>(
      `/procurement/supplier-slippage${buildQuery({ project_id: params.projectId, days: params.days ?? 90 })}`
    );
    return unwrap<SupplierSlippageRow[]>(response) ?? [];
  }

  async listSupervisorAssignments(params: { projectId?: string; isActive?: boolean } = {}): Promise<SupervisorTeamAssignment[]> {
    const response = await apiClient.get<Envelope<SupervisorTeamAssignment[]>>(
      `/procurement/supervisor-team-assignments${buildQuery({
        project_id: params.projectId,
        is_active: params.isActive,
      })}`
    );
    return unwrap<SupervisorTeamAssignment[]>(response) ?? [];
  }

  async createSupervisorAssignment(input: {
    projectId: string;
    supervisorUserId: string;
    teamId: string;
    startsAt?: string;
    endsAt?: string;
    isActive?: boolean;
  }): Promise<SupervisorTeamAssignment> {
    const response = await apiClient.post<Envelope<SupervisorTeamAssignment>>('/procurement/supervisor-team-assignments', input);
    return unwrap<SupervisorTeamAssignment>(response);
  }

  async deleteSupervisorAssignment(id: string): Promise<void> {
    await apiClient.delete(`/procurement/supervisor-team-assignments/${id}`);
  }
}

export default new ProcurementService();
